import React, { useEffect, useState } from 'react';
import { Save, Loader2, Target } from 'lucide-react';
import { useStrategyLabStore } from '../../stores/strategyLabStore';
import type {
  Strategy,
  StrategySide,
  SizingMode,
  ExitUnit,
  CreateStrategyRequest,
} from '../../types/strategyLab';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';

interface StrategyBuilderProps {
  editing: Strategy | null;
  onDone: () => void;
}

const SIZING_LABEL: Record<SizingMode, string> = {
  fixed_dollar: 'Fixed $ per trade',
  pct_of_budget: '% of daily budget',
  fixed_shares: 'Fixed shares',
};

const StrategyBuilder: React.FC<StrategyBuilderProps> = ({ editing, onDone }) => {
  const createStrategy = useStrategyLabStore((s) => s.createStrategy);
  const updateStrategy = useStrategyLabStore((s) => s.updateStrategy);
  const actionError = useStrategyLabStore((s) => s.actionError);
  const scanners = useStrategyLabStore((s) => s.scanners);
  const fetchScanners = useStrategyLabStore((s) => s.fetchScanners);

  const [name, setName] = useState('');
  const [scannerId, setScannerId] = useState('');
  const [side, setSide] = useState<StrategySide>('long');
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
  const [sizingMode, setSizingMode] = useState<SizingMode>('fixed_dollar');
  const [sizingValue, setSizingValue] = useState(2500);
  const [stopUnit, setStopUnit] = useState<ExitUnit>('pct');
  const [stopValue, setStopValue] = useState(3);
  const [targetUnit, setTargetUnit] = useState<ExitUnit>('pct');
  const [targetValue, setTargetValue] = useState(6);
  const [timeStop, setTimeStop] = useState<number | ''>(60);
  const [eodFlat, setEodFlat] = useState(true);
  const [maxConcurrent, setMaxConcurrent] = useState(3);
  const [maxTrades, setMaxTrades] = useState(10);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (scanners.length === 0) fetchScanners();
  }, [scanners.length, fetchScanners]);

  useEffect(() => {
    if (editing) {
      setName(editing.name);
      setScannerId(editing.scanner_id);
      setSide(editing.side);
      setOrderType(editing.entry?.order_type ?? 'market');
      setSizingMode(editing.sizing?.mode ?? 'fixed_dollar');
      setSizingValue(editing.sizing?.value ?? 2500);
      setStopUnit(editing.exits?.stop?.unit ?? 'pct');
      setStopValue(editing.exits?.stop?.value ?? 3);
      setTargetUnit(editing.exits?.profit_target?.unit ?? 'pct');
      setTargetValue(editing.exits?.profit_target?.value ?? 6);
      setTimeStop(editing.exits?.time_stop_minutes ?? '');
      setEodFlat(editing.exits?.eod_flat ?? true);
      setMaxConcurrent(editing.limits?.max_concurrent_positions ?? 3);
      setMaxTrades(editing.limits?.max_trades_per_day ?? 10);
    } else {
      setName('');
      setScannerId('');
    }
  }, [editing]);

  const canSave = name.trim().length > 0 && scannerId && sizingValue > 0 && stopValue > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const body: CreateStrategyRequest = {
      name: name.trim(),
      scanner_id: scannerId,
      side,
      entry: { order_type: orderType },
      sizing: { mode: sizingMode, value: sizingValue },
      exits: {
        stop: { unit: stopUnit, value: stopValue },
        profit_target: { unit: targetUnit, value: targetValue },
        time_stop_minutes: timeStop === '' ? null : Number(timeStop),
        eod_flat: eodFlat,
      },
      limits: {
        max_concurrent_positions: maxConcurrent,
        max_trades_per_day: maxTrades,
      },
    };
    const result = editing
      ? await updateStrategy(editing.id, body)
      : await createStrategy(body);
    setSaving(false);
    if (result) onDone();
  };

  const numField = (
    label: string, value: number, set: (n: number) => void, step = 1, min = 0,
  ) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input
        type="number" value={value} step={step} min={min}
        onChange={(e) => set(Number(e.target.value))}
      />
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Target className="w-4 h-4 text-primary" />
          {editing ? 'Edit strategy' : 'New strategy'}
        </CardTitle>
        <CardDescription>
          Entry fires on a scanner match; every entry places a broker-native bracket.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label className="text-xs">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Gap &amp; Go — small caps" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Entry scanner</Label>
            <Select value={scannerId} onValueChange={setScannerId}>
              <SelectTrigger><SelectValue placeholder="Pick a scanner…" /></SelectTrigger>
              <SelectContent>
                {scanners.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Direction</Label>
            <Select value={side} onValueChange={(v) => setSide(v as StrategySide)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="long">Long</SelectItem>
                <SelectItem value="short">Short</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Sizing</Label>
            <Select value={sizingMode} onValueChange={(v) => setSizingMode(v as SizingMode)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(SIZING_LABEL) as SizingMode[]).map((m) => (
                  <SelectItem key={m} value={m}>{SIZING_LABEL[m]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {numField(
            sizingMode === 'pct_of_budget' ? 'Percent (%)'
              : sizingMode === 'fixed_shares' ? 'Shares' : 'Dollars ($)',
            sizingValue, setSizingValue, sizingMode === 'pct_of_budget' ? 5 : 100,
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Stop</Label>
            <div className="flex gap-2">
              <Input
                type="number" value={stopValue} min={0} step={0.5} className="flex-1"
                onChange={(e) => setStopValue(Number(e.target.value))}
              />
              <Select value={stopUnit} onValueChange={(v) => setStopUnit(v as ExitUnit)}>
                <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pct">%</SelectItem>
                  <SelectItem value="atr">ATR</SelectItem>
                  <SelectItem value="price">$</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Target</Label>
            <div className="flex gap-2">
              <Input
                type="number" value={targetValue} min={0} step={0.5} className="flex-1"
                onChange={(e) => setTargetValue(Number(e.target.value))}
              />
              <Select value={targetUnit} onValueChange={(v) => setTargetUnit(v as ExitUnit)}>
                <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pct">%</SelectItem>
                  <SelectItem value="atr">ATR</SelectItem>
                  <SelectItem value="price">$</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs">Time stop (min)</Label>
            <Input
              type="number" value={timeStop} min={0}
              onChange={(e) => setTimeStop(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="none"
            />
          </div>
          {numField('Max positions', maxConcurrent, setMaxConcurrent)}
          {numField('Max trades/day', maxTrades, setMaxTrades)}
        </div>

        <div className="flex items-center justify-between rounded-md border border-border p-3">
          <div>
            <p className="text-sm font-medium">Flat by close</p>
            <p className="text-xs text-muted-foreground">Force-exit any open position before the bell.</p>
          </div>
          <Switch checked={eodFlat} onCheckedChange={setEodFlat} />
        </div>

        {actionError && <p className="text-sm text-rose-500">{actionError}</p>}

        <div className="flex gap-2 pt-1">
          <Button onClick={handleSave} disabled={!canSave || saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {editing ? 'Save changes' : 'Create strategy'}
          </Button>
          <Button variant="ghost" onClick={onDone}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default StrategyBuilder;
